import { Region } from '../../../lib/auth/region';
import color from '../../../style/color';
import { font } from '../../../style/text';
import { Dimensions, StyleSheet, ViewStyle } from 'react-native';

export const FINGER_POINTING_UP_EMOJI = '👆';

// Pixel dimensions of the conclusion (success) screen Amazon illustration asset. The card is sized to
// this aspect ratio so the image fills it exactly (no crop/letterbox). Update if the asset is re-exported.
export const ILLUSTRATION_ASPECT_RATIO = 1372 / 1280;

// Given at https://www.figma.com/design/R9LfFiwSeE34SRsAx3pn1Y/%F0%9F%92%A0-Foundations?node-id=2-722&t=yL9GDmhWhsb3Tu9i-0
export const newDesignSystemFontStyles = StyleSheet.create({
    titleXL: {
        fontFamily: font.ambitBold,
        fontSize: 22,
        lineHeight: 22 * 1.2,
        letterSpacing: 22 * -0.01,
    },
    // Headline/Medium foundation token, used for the conclusion (success) screen title only.
    headlineMedium: {
        fontFamily: font.wallopSansBold,
        fontSize: 24,
        lineHeight: 24 * 1.2,
        letterSpacing: 24 * -0.02,
    },
    actionMedium: {
        fontFamily: font.ambitBold,
        fontSize: 16,
        lineHeight: 20,
    },
    actionMediumUnderline: {
        fontFamily: font.ambitBold,
        fontSize: 16,
        lineHeight: 20,
        textDecorationLine: 'underline',
    },
    actionSmall: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 14 * 1.2,
    },
    displayMedium: {
        fontFamily: font.ambitBold,
        fontSize: 40,
        lineHeight: 40 * 1.1,
        letterSpacing: 40 * -0.02,
    },
    paragraphMedium: {
        fontFamily: font.ambitSemiBold,
        fontSize: 16,
        lineHeight: 16 * 1.2,
    },
    paragraphSmall: {
        fontFamily: font.ambitSemiBold,
        fontSize: 14,
        lineHeight: 14 * 1.2,
    },
    paragraphSmallBold: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 14 * 1.2,
    },
    paragraphXS: {
        fontFamily: font.ambitSemiBold,
        fontSize: 14, // TODO: this doesn't match the current design system spec
        lineHeight: 14 * 1.4,
    },
    // New-copy MSE A/B variant typography (Instrument Sans), per the Master Joko design tokens:
    // instruction rows = Body/14-medium, subtitle = Body/Medium.
    newCopyInstructionText: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        lineHeight: 14 * 1.4,
    },
    newCopySubtitleText: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        lineHeight: 14 * 1.2,
    },
});

export const styles = StyleSheet.create({
    container: {
        height: Dimensions.get('window').height,
        alignItems: 'center',
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 16,
        paddingBottom: 16,
    },
    containerButtons: {
        width: '100%',
        flex: 1,
        justifyContent: 'space-between',
    },
    containerHeader: {
        alignItems: 'center',
        textAlign: 'center',
        gap: 16,
    },
    containerHeaderText: {
        alignItems: 'center',
        gap: 12,
    },
    containerSplitInstructions: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        alignSelf: 'stretch',
    },
    containerStepper: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 20,
    },
    containerStep: {
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    containerStepTop: {
        flexDirection: 'row',
        alignItems: 'center',
    },

    stepLine: {
        width: normalizeWidth(50),
        height: 2,
        backgroundColor: color.snowyMint,
    },
    completedStep: {
        justifyContent: 'center',
        alignItems: 'center',
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: color.emerald,
        color: color.white,
    },
    currentStep: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: color.emerald,
    },
    nextStep: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: color.alto,
    },

    textStepLabel: {
        marginTop: 8,
        fontFamily: font.ambitBold,
        fontSize: normalizeWidth(14),
        color: color.alto,
    },
    containerVerticalStepper: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 12,
        width: normalizeWidth(180),
    },
    // Flex-fill connector bar between the two labels; 4px gap to each (via the container gap). Its colour is
    // set per-step in the component (black once the preceding step is completed, grey otherwise).
    verticalStepperConnector: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        height: 2,
        borderRadius: 10,
    },
    // The active step is a black rounded pill; the inactive step is plain grey text with no padding (so the
    // text sits 4px from the connector).
    verticalStepperPillActive: {
        backgroundColor: color.black,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 100,
    },
    verticalStepperLabelText: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 12,
        lineHeight: 12 * 1.4,
        color: color.silverChalice,
    },
    verticalStepperLabelTextActive: {
        color: color.white,
    },
    containerInstructionsWithMissingBannerButton: {
        gap: 12,
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    containerInstructions: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 12,
        gap: 12,
        borderRadius: 8,
        backgroundColor: color.alabaster,
        alignSelf: 'stretch',
    },
    containerMissingBannerButton: {
        borderBottomWidth: 1.5,
        borderBottomColor: 'rgba(0,0,0,0.16)',
    },
    containerCheckMark: {
        justifyContent: 'center',
        alignItems: 'center',
        height: 20,
        width: 20,
        borderRadius: 20,
    },
    containerTosConsentButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        alignSelf: 'stretch',
    },
    containerCheckMarkSelected: {
        backgroundColor: color.emerald,
    },
    containerCheckMarkUnselected: {
        borderWidth: 2,
        borderColor: color.silverChalice,
    },
    containerWaitingScreen: {
        flex: 1,
        minHeight: '100lvh',
        justifyContent: 'center',
        alignItems: 'center',
    },
    containerGreenCircleBackground: {
        height: normalizeHeight(300),
        width: normalizeHeight(300),
        borderRadius: normalizeHeight(300),
        backgroundColor: color.snowyMint,
        justifyContent: 'center',
        alignItems: 'center',
    },
    imageCheckMarkIcon: {
        height: 16,
        width: 16,
        resizeMode: 'contain',
    },
    logoHeaderIcon: {
        width: 36,
        height: 36,
    },
    imageTickCircleIcon: {
        width: 17,
        height: 16,
    },
    warningHeaderIcon: {
        width: 48,
        height: 48,
    },
    pointingEmoji: {
        position: 'absolute',
        right: normalizeWidth(24),
        top: 4,
        fontSize: 40,
        zIndex: 1,
    },
    body: {
        justifyContent: 'space-between',
        flexGrow: 1,
        alignSelf: 'stretch',
    },
    // Grey rounded card holding the conclusion (success) screen Amazon illustration.
    // flexShrink lets the card give up height on short viewports so it never extends under the fixed CTA.
    conclusionIllustrationContainer: {
        position: 'relative',
        alignSelf: 'stretch',
        // Size the card to the illustration's aspect ratio off the card width so the image fills it
        // exactly (no crop, no letterbox) and the pulse overlay can be placed by %.
        height: (Dimensions.get('window').width - 48) / ILLUSTRATION_ASPECT_RATIO,
        flexShrink: 1,
        backgroundColor: color.concrete,
        borderRadius: 16,
        overflow: 'hidden',
    },
    conclusionIllustration: {
        width: '100%',
        height: '100%',
        resizeMode: 'cover',
    },
});

export function getImageActivationPreambleStyle(region: Region) {
    return {
        height: normalizeHeight(region === Region.FR ? 204 : 172),
        width: normalizeHeight(325),
    };
}

export function containerStyleWithGap(gap: number) {
    return [styles.container, { gap } as ViewStyle];
}

export function normalizeHeight(size: number, ratioEffect: number = 1, maxSize?: number): number {
    const deviceSize = Dimensions.get('window').height;
    const standardDeviceHeight = 637; // standard for Figma wire frames in the revamped MSE onboarding flow
    return scaleToUserDeviceSize(size, deviceSize, standardDeviceHeight, ratioEffect, maxSize);
}

export function normalizeWidth(size: number, ratioEffect: number = 1, maxSize?: number): number {
    const deviceSize = Dimensions.get('window').width;
    const standardDeviceWidth = 375; // standard for Figma wire frames in the revamped MSE onboarding flow
    return scaleToUserDeviceSize(size, deviceSize, standardDeviceWidth, ratioEffect, maxSize);
}

function scaleToUserDeviceSize(
    size: number,
    deviceSize: number,
    standardDeviceSize: number,
    ratioEffect: number = 1,
    maxSize?: number
): number {
    const ratio = deviceSize / standardDeviceSize;
    const normalizedSize = Math.round(((ratio - 1) * ratioEffect + 1) * size);
    if (maxSize) return Math.min(normalizedSize, maxSize);
    else return normalizedSize;
}
